import React, { CSSProperties } from 'react';
import * as ReactDOMServer from 'react-dom/server';
import { MapContainer, Marker, Popup, Rectangle, TileLayer, Tooltip } from 'react-leaflet';
import { Hotline } from 'react-leaflet-hotline';

import {
  DivIcon,
  Icon,
  IconOptions,
  LatLng,
  latLng,
  LatLngBounds,
  latLngBounds,
  Map as LeafletMap,
  point,
} from 'leaflet';

import { LatLng as LogLatLng, Log } from '@/parsing/Log';

import { MapEventInfo } from '../timeline/TimelineData';
import { LeafletRuler } from './LeafletRuler/LeafletRuler';
import { pathColorPalette } from './PathColorPalette';

type Props = {
  width: number;
  height: number;
  style?: CSSProperties;

  log: Log;
};

type MapMarker = {
  lat: number;
  lng: number;
  tooltipText?: string;
  popupText?: string;
  icon?: Icon<IconOptions> | DivIcon;
};

type State = {
  fixedMarkers: MapMarker[];
  selectedMarker: MapMarker | undefined;
  mapContext:
    | {
        mapEventInfo: MapEventInfo;
        boundsWithoutPadding: LatLngBounds;
      }
    | undefined;
  mapReady: boolean;
};

export class Map extends React.Component<Props, State> {
  private leafletMap = React.createRef<LeafletMap>();

  private iconPickup = new DivIcon({
    html: ReactDOMServer.renderToString(
      <div
        style={{
          width: '36px',
          height: '36px',
          background: 'transparent url(/static/marker-pickup.svg) no-repeat',
        }}
      />,
    ),
    iconAnchor: [18, 12],
  });

  private iconDestination = new DivIcon({
    html: ReactDOMServer.renderToString(
      <div
        style={{
          width: '36px',
          height: '36px',
          background: 'transparent url(/static/marker-dst.svg) no-repeat',
        }}
      />,
    ),
    iconAnchor: [18, 12],
  });

  private getIconSelected(color: string): DivIcon {
    return new DivIcon({
      html: `<svg color="${color}">
        <use xlink:href="/static/marker-black.svg#marker-black" />
        </svg>`,
      iconAnchor: [11, 33],
    });
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      fixedMarkers: [],
      selectedMarker: undefined,
      mapContext: undefined,
      mapReady: false,
    };
  }

  selectLocation(args: { location: LogLatLng; color: string | undefined }) {
    this.setState({
      selectedMarker: {
        lat: args.location.lat,
        lng: args.location.lng,
        icon: this.getIconSelected(args.color || '#000'),
      },
    });

    const bounds = latLng(args.location.lat, args.location.lng).toBounds(100);

    if (this.leafletMap.current) {
      this.leafletMap.current.options.zoomSnap = 0;
      this.leafletMap.current.flyToBounds(bounds);
      this.leafletMap.current.options.zoomSnap = 1;
    }
  }

  setMapContext(mapEventInfo: MapEventInfo | undefined) {
    const same = this.state.mapContext?.mapEventInfo.logEntryIndex === mapEventInfo?.logEntryIndex;
    if (same) {
      this.setState({
        mapContext: undefined,
      });
      return;
    }

    if (mapEventInfo) {
      const bounds = latLngBounds(mapEventInfo.viewportBounds.sw, mapEventInfo.viewportBounds.ne);

      const animationDurationSec = 0.3;
      this.leafletMap.current!.options.zoomSnap = 0;
      this.leafletMap.current!.flyToBounds(bounds, {
        padding: point(8, 8),
        duration: animationDurationSec,
      });
      setTimeout(() => {
        this.leafletMap.current!.options.zoomSnap = 1;
      }, animationDurationSec * 1000.0);

      const h = mapEventInfo.viewportBounds.ne.lat - mapEventInfo.viewportBounds.sw.lat;
      const w = mapEventInfo.viewportBounds.ne.lng - mapEventInfo.viewportBounds.sw.lng;

      // TODO: make it hemisphere-agnostic.
      const neWithPadding = latLng(
        mapEventInfo.viewportBounds.ne.lat - h * (mapEventInfo.padding.top / mapEventInfo.frame.h),
        mapEventInfo.viewportBounds.ne.lng -
          w * (mapEventInfo.padding.right / mapEventInfo.frame.w),
      );
      const swWithPadding = latLng(
        mapEventInfo.viewportBounds.sw.lat +
          h * (mapEventInfo.padding.bottom / mapEventInfo.frame.h),
        mapEventInfo.viewportBounds.sw.lng + w * (mapEventInfo.padding.left / mapEventInfo.frame.w),
      );

      const boundsWithoutPadding = latLngBounds(swWithPadding, neWithPadding);

      this.setState({
        mapContext: {
          mapEventInfo,
          boundsWithoutPadding,
        },
      });
    } else {
      this.setState({ mapContext: undefined });
    }
  }

  componentDidMount() {
    this.regenerateMarkersInState();

    // https://github.com/PaulLeCam/react-leaflet/issues/156
    setTimeout(() => {
      if (this.leafletMap.current) {
        this.leafletMap.current.invalidateSize();
        this.setState({ mapReady: true });
        this.resetMapViewport();
      }
    }, 100);
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.width !== this.props.width || prevProps.height !== this.props.height) {
      Array.from(document.getElementsByClassName('leaflet-container')).forEach((el) => {
        (el as HTMLElement).style.height = `${this.props.height}px`;
      });

      this.leafletMap.current?.invalidateSize();
    }

    if (prevProps.log.uniqueId !== this.props.log.uniqueId) {
      this.regenerateMarkersInState();

      setTimeout(() => {
        this.resetMapViewport();
      }, 100);
    }
  }

  private resetMapViewport() {
    if (!this.leafletMap.current) {
      return;
    }

    if (this.state.fixedMarkers.length > 0) {
      this.leafletMap.current.fitBounds(this.state.fixedMarkers.map((m) => [m.lat, m.lng]));
    } else if (this.props.log.gpsHistory.locations.length > 0) {
      this.leafletMap.current.fitBounds(
        this.props.log.gpsHistory.locations.map((l) => [l.lat, l.lng]),
      );
    } else {
      this.leafletMap.current.fitWorld();
    }
  }

  render() {
    return (
      <div
        style={{
          ...this.props.style,
          width: this.props.width,
          height: this.props.height,
        }}
      >
        <MapContainer
          ref={this.leafletMap}
          style={{
            width: this.props.width,
            height: this.props.height,
          }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {this.state.selectedMarker ? (
            <Marker
              position={new LatLng(this.state.selectedMarker.lat, this.state.selectedMarker.lng)}
              icon={this.state.selectedMarker.icon}
            >
              {this.state.selectedMarker.tooltipText ? (
                <Tooltip>{this.state.selectedMarker.tooltipText}</Tooltip>
              ) : null}
              {this.state.selectedMarker.popupText ? (
                <Popup>{this.state.selectedMarker.popupText}</Popup>
              ) : null}
            </Marker>
          ) : null}

          {this.state.mapContext ? (
            <>
              <Rectangle
                bounds={latLngBounds(
                  this.state.mapContext.mapEventInfo.viewportBounds.sw,
                  this.state.mapContext.mapEventInfo.viewportBounds.ne,
                )}
                pathOptions={{
                  color: '#995555',
                }}
              />
              <Rectangle
                bounds={this.state.mapContext.boundsWithoutPadding}
                pathOptions={{
                  color: '#559955',
                }}
              />
            </>
          ) : null}

          {this.markers()}
          {this.paths()}

          <LeafletRuler />
        </MapContainer>
      </div>
    );
  }

  private paths(): React.JSX.Element | null {
    if (
      !this.state.mapReady ||
      this.props.log.gpsHistory.locations.length === 0 ||
      this.leafletMap.current === null
    ) {
      return null;
    }

    return (
      <Hotline
        data={this.props.log.gpsHistory.locations.map((l, index) => ({ ...l, index }))}
        getLat={({ point }) => point.lat}
        getLng={({ point }) => point.lng}
        getVal={({ point }) => point.index}
        options={{
          min: 0,
          max: this.props.log.gpsHistory.locations.length - 1,
          weight: 4,
          outlineWidth: 0,
          palette: pathColorPalette(),
        }}
      />
    );
  }

  private regenerateMarkersInState() {
    const markers: MapMarker[] = [];

    this.props.log.foundOrders.forEach((order) => {
      const pickup = {
        lat: order.pickup.lat,
        lng: order.pickup.lng,
        tooltipText: `Pickup: ${order.pickup.address}`,
        icon: this.iconPickup,
      };
      if (this.props.log.foundOrders.length > 1) {
        pickup.tooltipText += ` (order ${order.orderId})`;
      }

      markers.push(pickup);

      order.destinations.forEach((d, index) => {
        let tooltipText =
          order.destinations.length === 1
            ? `Destination: ${d.address}`
            : `Destination #${index + 1}: ${d.address}`;

        if (this.props.log.foundOrders.length > 1) {
          tooltipText += ` (order ${order.orderId})`;
        }

        markers.push({
          lat: d.lat,
          lng: d.lng,
          icon: this.iconDestination,
          tooltipText,
        });
      });
    });

    this.setState({
      fixedMarkers: markers,
    });
  }

  private markers(): React.JSX.Element[] {
    const result: React.JSX.Element[] = [];
    this.state.fixedMarkers.forEach((marker, index) => {
      result.push(
        <Marker key={index} icon={marker.icon} position={new LatLng(marker.lat, marker.lng)}>
          {marker.tooltipText ? <Tooltip>{marker.tooltipText}</Tooltip> : null}
          {marker.popupText ? <Popup>{marker.popupText}</Popup> : null}
        </Marker>,
      );
    });
    return result;
  }
}
